import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_GEO_RESTRICTION_MESSAGE } from "../constants/geoRestriction";
import { fetchGeoAccess } from "../utils/fetchGeoAccess";
import { isAdminHost, isLocalDevHost, isAdminPath } from "../utils/adminHost";
import GeoRestrictedScreen from "./GeoRestrictedScreen";
import "../geo-restriction.css";

const SKIP_ENV = process.env.REACT_APP_SKIP_GEO_GATE === "true";

function shouldSkipGeoGate() {
  if (SKIP_ENV) return true;
  if (isAdminHost()) return true;
  if (typeof window !== "undefined" && isLocalDevHost() && isAdminPath(window.location.pathname)) {
    return true;
  }
  return false;
}

const GeoGate = ({ children }) => {
  const skip = useMemo(() => shouldSkipGeoGate(), []);
  const [status, setStatus] = useState(skip ? "allowed" : "loading");
  const [retrying, setRetrying] = useState(false);
  const [payload, setPayload] = useState(null);

  const runCheck = useCallback(
    async ({ isRetry = false, signal } = {}) => {
      if (skip) {
        setStatus("allowed");
        return;
      }

      if (isRetry) setRetrying(true);
      else setStatus("loading");

      try {
        const result = await fetchGeoAccess({ signal });
        setPayload(result);

        if (!result.restrictionEnabled || result.allowed) {
          setStatus("allowed");
          return;
        }

        setStatus("blocked");
      } catch (err) {
        if (err?.name === "AbortError") return;

        const onLocal =
          typeof window !== "undefined" &&
          /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

        if (onLocal) {
          setStatus("allowed");
          return;
        }

        setPayload({
          allowed: false,
          restrictionEnabled: true,
          message: DEFAULT_GEO_RESTRICTION_MESSAGE,
          licensedRegions: ["RW"],
          country: null,
        });
        setStatus("blocked");
      } finally {
        if (isRetry) setRetrying(false);
      }
    },
    [skip]
  );

  useEffect(() => {
    if (skip) return undefined;
    const controller = new AbortController();
    runCheck({ signal: controller.signal });
    return () => controller.abort();
  }, [runCheck, skip]);

  if (status === "loading") {
    return (
      <div className="geo-gate-loading" role="status" aria-live="polite">
        <div className="geo-gate-loading-mark" aria-hidden />
        <p className="geo-gate-loading-copy">Checking your region…</p>
      </div>
    );
  }

  if (status === "blocked") {
    return (
      <GeoRestrictedScreen
        message={payload?.message || DEFAULT_GEO_RESTRICTION_MESSAGE}
        country={payload?.country}
        licensedRegions={payload?.licensedRegions}
        onRetry={() => runCheck({ isRetry: true })}
        retrying={retrying}
      />
    );
  }

  return children;
};

export default GeoGate;
